import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { fetchTests } from "../../actions/testActions";
import ExamCards from "./ExamCards";
import { Spinner } from "react-bootstrap";
import SEO from "../common/SEO";
import { slug as slugify } from 'github-slugger';
import { FiBookOpen, FiFolder, FiCheckCircle, FiArrowLeft, FiMenu, FiX } from 'react-icons/fi';
import "./subjectwisecards.css";

function SubjectWiseTestFilter() {
    const { series } = useParams();
    const dispatch = useDispatch();
    const testsData = useSelector(state => state.tests.tests);
    const loading = useSelector(state => state.tests.loading);
    const studentProfileData = useSelector(state => state.dashboard.userData);
    const paginated = false;

    const [selectedSubject, setSelectedSubject] = useState(null);
    const [selectedTopic, setSelectedTopic] = useState(null);
    const [showMobileSidebar, setShowMobileSidebar] = useState(false);

    useEffect(() => {
        if (!testsData || testsData.length === 0 || testsData.length === 10) {
            dispatch(fetchTests(1, 100, null, '', paginated));
        }
    }, [dispatch, testsData]);

    useEffect(() => {
        setSelectedSubject(null);
        setSelectedTopic(null);
    }, [series]);

    // Filter exams for this test series that have subject and topic defined
    const seriesExams = (testsData || []).filter((exam) =>
        exam?.testSeries?.toLowerCase() === series?.toLowerCase() && exam?.subject && exam?.topic
    );

    // Extract unique subjects
    const subjects = Array.from(new Set(seriesExams.map(exam => exam.subject).filter(Boolean)));

    // Topics for the currently selected subject
    const topicsForSelectedSubject = selectedSubject
        ? Array.from(new Set(seriesExams.filter(exam => exam.subject.toLowerCase() === selectedSubject.toLowerCase()).map(exam => exam.topic).filter(Boolean)))
        : [];

    // Auto-select first topic when subject changes
    const handleSelectSubject = (subject) => {
        setSelectedSubject(subject);
        const subTopics = Array.from(new Set(seriesExams.filter(exam => exam.subject.toLowerCase() === subject.toLowerCase()).map(exam => exam.topic).filter(Boolean)));
        if (subTopics.length > 0) {
            setSelectedTopic(subTopics[0]);
        } else {
            setSelectedTopic(null);
        }
    };

    const navigate = useNavigate();
    const handleAttemptTest = async (test) => {
        const isAuthenticated = Boolean(localStorage.getItem("userId"));
        if (isAuthenticated) {
            navigate(`/exam-terms-conditions`, { state: { test, studentProfileData } });
        } else {
            navigate('/signin');
        }
    };

    // Exams matching currently selected subject & topic
    const currentExams = selectedSubject && selectedTopic
        ? seriesExams.filter(exam => exam.subject.toLowerCase() === selectedSubject.toLowerCase() && exam.topic.toLowerCase() === selectedTopic.toLowerCase())
        : [];

    const generateSeoData = () => {
        const seriesName = (series || '').toUpperCase();
        let title = `${seriesName} Subject-Wise & Topic-Wise Test Series | My Exam World`;
        let description = `Prepare for ${seriesName} with subject-wise and topic-wise online test series and practice mock exams on My Exam World.`;
        let keywords = `${seriesName} subject wise tests, ${seriesName} topic wise practice, online test series, MPSC mock test`;

        if (selectedSubject && selectedTopic) {
            title = `${selectedSubject} - ${selectedTopic} Mock Test (${seriesName}) | My Exam World`;
            description = `Practice ${selectedSubject} - ${selectedTopic} online mock test for ${seriesName}. Attempt practice questions with detailed explanations.`;
            keywords = `${selectedSubject} mock test, ${selectedTopic} test, ${seriesName} ${selectedSubject}, ${selectedTopic} questions`;
        } else if (selectedSubject) {
            title = `${selectedSubject} Subject Test Series (${seriesName}) | My Exam World`;
            description = `Explore topic-wise online practice tests for ${selectedSubject} in ${seriesName} exam series.`;
            keywords = `${selectedSubject} test series, ${selectedSubject} topic wise questions, ${seriesName} ${selectedSubject}`;
        }

        return {
            title,
            description,
            keywords,
            url: `/subject-wise-series/${(series || '').toLowerCase()}`
        };
    };

    const seoData = generateSeoData();

    return (
        <>
            <SEO
                title={seoData.title}
                description={seoData.description}
                keywords={seoData.keywords}
                url={seoData.url}
            />

            {loading ? (
                <div className="d-flex justify-content-center align-items-center" style={{ height: '40vh', color: '#0096c7' }}>
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                </div>
            ) : (
                <div className="container-fluid px-3 px-md-4 my-4">
                    {/* Header */}
                    <div className="text-center my-2">
                        <h2 className="popular-Test text-uppercase m-0">{series} SUBJECT & TOPIC WISE TESTS</h2>
                        <p className="text-muted small m-0">Select a subject first, then explore topics and available tests</p>
                    </div>

                    {/* STEP 1: SELECT SUBJECT VIA CARDS (If no subject is selected) */}
                    {!selectedSubject ? (
                        <div className="test-series-container pt-2">
                            <h4 className="text-center fw-bold main-color mb-3 d-flex align-items-center justify-content-center gap-2">
                                <FiBookOpen /> Select a Subject to Begin
                            </h4>
                            {subjects.length > 0 ? (
                                <section className="test-series-status">
                                    <div className="row">
                                        {subjects.map((subject, idx) => {
                                            const count = seriesExams.filter(e => e.subject.toLowerCase() === subject.toLowerCase()).length;
                                            const topicCount = Array.from(new Set(seriesExams.filter(e => e.subject.toLowerCase() === subject.toLowerCase()).map(e => e.topic))).length;

                                            return (
                                                <div
                                                    key={idx}
                                                    className="test-series-box"
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => handleSelectSubject(subject)}
                                                >
                                                    <div className="p-3 rounded-circle mb-2" style={{ width: '60px', height: '60px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0, 150, 199, 0.1)', color: '#0096c7' }}>
                                                         <FiBookOpen size={28} />
                                                     </div>
                                                     <div className="test-series-text">
                                                         <p className="text-capitalize fw-bold text-dark mb-1">{subject}</p>
                                                         <span className="badge bg-light text-dark border me-1">{topicCount} Topics</span>
                                                        <span className="badge button-design">{count} Tests</span>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </section>
                            ) : (
                                <div className="text-center p-5 bg-white rounded-3 shadow-sm border">
                                    <p className="text-muted fs-5 mb-0">No subject-wise tests uploaded yet for {series.toUpperCase()}.</p>
                                </div>
                            )}
                        </div>
                    ) : (
                        /* STEP 2: SUBJECT IS SELECTED -> SHOW TOPICS SIDEBAR ON LEFT & TESTS ON RIGHT */
                        <div>
                            {/* Navigation Bar */}
                            <div className="d-flex align-items-center justify-content-between bg-white p-3 rounded-3 shadow-sm border mb-4">
                                <div className="d-flex align-items-center gap-3">
                                    <button 
                                        className="btn change-subject-btn d-flex align-items-center gap-1"
                                        onClick={() => {
                                            setSelectedSubject(null);
                                            setSelectedTopic(null);
                                        }}
                                    >
                                        <FiArrowLeft /> Change Subject
                                    </button>
                                    <span className="fw-bold fs-5 text-dark m-0">
                                        Subject: <span className="main-color text-capitalize">{selectedSubject}</span>
                                    </span>
                                </div>

                                {/* Mobile Sidebar Toggle */}
                                <button 
                                    className="btn button-design btn-sm d-md-none d-flex align-items-center gap-1"
                                    onClick={() => setShowMobileSidebar(!showMobileSidebar)}
                                >
                                    <FiFolder /> Topics {showMobileSidebar ? <FiX /> : <FiMenu />}
                                </button>
                            </div>

                            <div className="row g-4">
                                {/* LEFT SIDEBAR: TOPICS ONLY */}
                                <div className={`col-lg-3 col-md-4 ${showMobileSidebar ? 'd-block' : 'd-none d-md-block'}`}>
                                    <div className="w3-sidebar-menu bg-white rounded-3 shadow-sm border p-3">
                                        <h5 className="fw-bold mb-3 main-color d-flex align-items-center justify-content-between border-bottom pb-2">
                                            <span className="d-flex align-items-center gap-2">
                                                <FiFolder /> Topics ({topicsForSelectedSubject.length})
                                            </span>
                                            <span className="d-md-none" onClick={() => setShowMobileSidebar(false)}>
                                                <FiX style={{ cursor: 'pointer' }} />
                                            </span>
                                        </h5>

                                        {topicsForSelectedSubject.length > 0 ? (
                                            <div className="topic-list">
                                                {topicsForSelectedSubject.map((topic, tIdx) => {
                                                    const isTopicSelected = selectedTopic?.toLowerCase() === topic.toLowerCase();
                                                    const count = seriesExams.filter(e => e.subject.toLowerCase() === selectedSubject.toLowerCase() && e.topic.toLowerCase() === topic.toLowerCase()).length;

                                                    return (
                                                        <div
                                                            key={tIdx}
                                                            className={`topic-list-item d-flex align-items-center justify-content-between cursor-pointer ${isTopicSelected ? 'topic-item-active' : ''}`}
                                                            style={{ cursor: 'pointer' }}
                                                            onClick={() => {
                                                                setSelectedTopic(topic);
                                                                setShowMobileSidebar(false);
                                                            }}
                                                        >
                                                            <span className="fw-semibold">{topic}</span>
                                                            <span className={`badge ${isTopicSelected ? 'bg-white text-dark' : 'bg-light text-dark border'}`}>{count}</span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            <p className="text-muted small my-2">No topics found for this subject.</p>
                                        )}
                                    </div>
                                </div>

                                {/* RIGHT MAIN CONTENT AREA: TESTS FOR SELECTED TOPIC */}
                                <div className="col-lg-9 col-md-8">
                                    <div className="bg-white rounded-3 shadow-sm border p-3 p-md-4 min-vh-50">
                                        {selectedTopic ? (
                                            <div>
                                                <div className="border-bottom pb-3 mb-4">
                                                    <h4 className="fw-bold main-color mb-1 d-flex align-items-center gap-2">
                                                        <FiCheckCircle /> Topic: {selectedTopic}
                                                    </h4>
                                                    <span className="text-muted">Available practice tests for <strong>{selectedTopic}</strong></span>
                                                </div>

                                                <div className="row justify-content-center justify-content-md-start">
                                                    {currentExams.length > 0 ? (
                                                        currentExams.map((test, index) => (
                                                            <ExamCards
                                                                key={index}
                                                                index={index}
                                                                test={test}
                                                                onAttemptTest={handleAttemptTest}
                                                            />
                                                        ))
                                                    ) : (
                                                        <div className="text-center py-5">
                                                            <p className="text-muted fs-5">No tests available for this topic yet.</p>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="text-center py-5">
                                                <p className="text-muted fs-5">Select a topic from the left sidebar to view tests.</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </>
    );
}

export default SubjectWiseTestFilter;
